/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpError } from 'react-admin';

import * as PluginService from '@src/services/plugins';
import {
  ICreateConfigVersionParams,
  IGetConfigVersionListParams,
  IUpdateConfigVersionParams,
  pluginResourceTypeMap,
  PluginsDataProvider,
} from '@src/types';

import {
  clientPaginate,
  clientSort,
  requirePartner,
} from '../shared';

export const pluginsDataProvider: PluginsDataProvider = {
  getList: async (resource, params) => {
    try {
      const { pagination, sort } = params as IGetConfigVersionListParams;

      const { code } = requirePartner();
      const { page, perPage } = pagination || {};
      const { field, order } = sort || {};

      const pluginType = pluginResourceTypeMap[resource];

      const configVersions = await PluginService.getConfigVersions({
        partnerCode: code,
        pluginType,
      });
      const sortedConfigVersions = clientSort(
        configVersions,
        configVersion => configVersion[field],
        order,
      );
      const paginatedConfigVersions = clientPaginate(
        sortedConfigVersions,
        page,
        perPage,
      );

      return {
        data: paginatedConfigVersions as any,
        total: configVersions.length,
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError('Failed to get config version list.', 500);
    }
  },

  getOne: async (resource, params) => {
    try {
      const { id } = params;

      const { code } = requirePartner();

      const pluginType = pluginResourceTypeMap[resource];

      const configVersion = await PluginService.getConfigVersion({
        id: `${id}`,
        partnerCode: code,
        pluginType,
      });

      const configuration = await PluginService.getPluginSettings({
        partnerCode: code,
        pluginType,
        id: `${id}`,
      });

      const settings = JSON.stringify(configuration, null, 2);

      return {
        data: {
          configVersion,
          settings,
          id,
        } as any,
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError('Failed to get one config version.', 500);
    }
  },

  getMany: async (_resource, _params) => {
    throw new Error('getMany not implemented');
  },

  getManyReference: async (_resource, _params) => {
    throw new Error('getManyReference not implemented');
  },

  create: async (resource, params) => {
    try {
      const { data } = params as ICreateConfigVersionParams;
      const { name } = data;

      const { code } = requirePartner();

      const pluginType = pluginResourceTypeMap[resource];

      const configVersion = await PluginService.postConfigVersion({
        configVersion: {
          name,
        },
        partnerCode: code,
        pluginType,
      });

      return {
        data: configVersion as any,
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError('Failed to create config version.', 500);
    }
  },

  update: async (resource, params) => {
    try {
      const { data } = params as IUpdateConfigVersionParams;
      const {
        settings,
        configVersion: { name },
        id,
      } = data;

      const { code } = requirePartner();

      const pluginType = pluginResourceTypeMap[resource];

      const updatedConfigVersion = await PluginService.patchConfigVersion({
        configVersion: {
          name,
        },
        id,
        partnerCode: code,
        pluginType,
      });

      const replacedSettings = await PluginService.putPluginSettings({
        id,
        partnerCode: code,
        pluginType,
        settings: JSON.parse(settings),
      });

      return {
        data: {
          configVersion: updatedConfigVersion,
          id,
          settings: replacedSettings,
        },
      } as any;
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError('Failed to update config version.', 500);
    }
  },

  updateMany: async (_resource, _params) => {
    throw new Error('updateMany not implemented');
  },

  delete: async (resource, params) => {
    try {
      const { id } = params;

      const { code } = requirePartner();

      const pluginType = pluginResourceTypeMap[resource];

      const configuration = await PluginService.deleteConfigVersion({
        id: `${id}`,
        partnerCode: code,
        pluginType,
      });

      return {
        data: configuration as any,
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError('Failed to delete config version.', 500);
    }
  },

  deleteMany: async (_resource, _params) => {
    throw new Error('deleteMany not implemented');
  },

  duplicateConfigVersion: async (resource, params) => {
    try {
      const { data } = params;
      const { name, sourceVersion } = data;

      const { code } = requirePartner();

      const pluginType = pluginResourceTypeMap[resource];

      const configVersion = await PluginService.postConfigVersion({
        configVersion: {
          name,
          sourceVersion,
        },
        partnerCode: code,
        pluginType,
      });

      return configVersion;
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError('Failed to duplicate config version.', 500);
    }
  },

  setDefaultConfigVersion: async (resource, params) => {
    try {
      const { data } = params;
      const { id } = data;

      const { code } = requirePartner();

      const pluginType = pluginResourceTypeMap[resource];

      const configVersion = await PluginService.setDefaultConfigVersion({
        id,
        partnerCode: code,
        pluginType,
      });

      return configVersion;
    } catch (error) {
      if (error instanceof HttpError) throw error;
      throw new HttpError('Failed to set default config version.', 500);
    }
  }
};